import { Component, HostListener, OnInit, computed, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin } from 'rxjs';
import { CanDeactivateComponent } from './survey-builder.guard';
import { ChoiceModel, QuestionModel } from '../../models/question.model';
import { QuestionType } from '../../models/questionType.enum';
import { getQuestionTypeLabel, QUESTION_TYPE_METAS } from '../../metadatas/questionType.metadata';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { LayoutSidebarService } from '../../../../shared/services/layout-sidebar.service';
import { ActivatedRoute } from '@angular/router';
import { ApiSurveyModelService, ApiSurveyDto } from '../../../../shared/services/api-surveymodel.service';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { ApiSurveyTranslationsService } from '../../../../shared/services/api-surveytranslations.service';
import { SurveyTranslationsResponse } from '../../../../shared/models/surveytranslations-response.interface';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { SupportedLanguage } from '../../../../shared/models/supportedLanguage.interface';


@Component({
  selector: 'app-survey-builder',
  standalone: true,
  imports: [BreadcrumbComponent, PageLoaderComponent, FormsModule, NgTemplateOutlet, SurveyHeaderComponent, DragDropModule],
  templateUrl: './survey-builder.component.html',
})

export class SurveyBuilderComponent implements OnInit, CanDeactivateComponent {
  translations: SurveyTranslationsResponse | null = null;
  survey: ApiSurveyDto | null = null;
  questions: QuestionModel[] = [];
  loading = signal(false);
  error = signal<string | null>(null);
  readonly QuestionType = QuestionType;
  readonly QuestionTypeMeta = QUESTION_TYPE_METAS;
  readonly getQuestionTypeLabel = getQuestionTypeLabel;
  // Types configured with the basic fields only (no placeholder / max length) plus Not Applicable.
  readonly basicQuestionTypes: QuestionType[] = [QuestionType.CustomizedI, QuestionType.CustomizedII, QuestionType.BrandModel];
  private readonly naQuestionTypes: QuestionType[] = [QuestionType.Numeric, ...this.basicQuestionTypes];
  selectedQuestionId = "";
  openSurveyMenu: boolean = false;
  openAddMenu: boolean = false;
  openMenuQuestionId: string | null = null;
  defaultLanguage: string = 'en';
  supportedLanguages: Array<{ lang: string } & SupportedLanguage> = [];

  // delete
  questionPendingDelete = signal<QuestionModel | null>(null);
  deleting = signal(false);
  deleteError = signal<string | null>(null);

  // change tracking
  private originalQuestions = new Map<string, string>();

  // save
  showSaveConfirm = signal(false);
  saving = signal(false);
  saveError = signal<string | null>(null);

  // discard
  showDiscardConfirm = signal(false);

  // unsaved-changes leave guard
  showLeaveConfirm = signal(false);
  private leaveResolve: ((allow: boolean) => void) | null = null;

  readonly canEdit = computed(() => this.userStore.isAdmin());
  readonly canEditQuestionCode = computed(() => this.userStore.isSuperAdmin());

  constructor(
    private route: ActivatedRoute,
    private organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private surveyModelService: ApiSurveyModelService,
    private surveyTranslationsService: ApiSurveyTranslationsService,
    private sidebarService: LayoutSidebarService) {}

  ngOnInit() {
    this.sidebarService.setFullscreen(true);

    // surveySelectedGuard on the route guarantees these are present and valid.
    const organizationId = this.organizationStore.selectedOrganizationId()!;
    const surveyId = this.route.snapshot.paramMap.get('id')!;

    this.loadSurveyModel(organizationId, surveyId);
  }

  ngOnDestroy(): void {
    this.sidebarService.setFullscreen(false);
  }


  private loadSurveyModel(organizationId: string, surveyId: string): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      survey: this.surveyModelService.getSurveyModel(organizationId, surveyId),
      translations: this.surveyTranslationsService.getSurveyTranslations(organizationId,surveyId)
    }).subscribe({
      next: ({ survey, translations }) => {
        // Survey Model
        this.survey = survey;
        this.questions = Object.entries(this.survey.questions).map(([key, value]) => ({
          ...value,
          questionCode: key,
          previousQuestionCode: key,
          skipCondition: !value.skipCondition || Object.keys(value.skipCondition).length === 0 ? null : value.skipCondition,
          //skipCondition: !value.skipCondition || Object.keys(value.skipCondition).length === 0 ? null : Array.from(value.skipCondition.entries()).map(([key, value]) => ({key,value}))
        }));
        //Sort questions by questionOrder
        this.questions.sort((a, b) => a.questionOrder - b.questionOrder);
        this.selectedQuestionId = this.questions[0]?.questionCode;

        // Translations
        this.translations = translations;
        this.defaultLanguage = ['en', 'en-GB', 'en-US'].find(key => key in translations.supportedLanguages) ?? Object.keys(translations.supportedLanguages)[0];
        this.supportedLanguages = Object.entries(translations.supportedLanguages).map(([key, value]) => ({
          lang: key,
          ...value
        }));

        this.changeTranslations();
        this.takeSnapshot();
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load survey data. | ' + err);
        this.loading.set(false);
      }
    });
  }

  get selectedQuestion(): QuestionModel | undefined {
    return this.questions.find(q => q.questionCode === this.selectedQuestionId);
  }

  selectQuestion(question: QuestionModel): void {
    this.selectedQuestionId = question.questionCode;
  }

  onQuestionCodeChange(question: QuestionModel, newCode: string): void {
    if (!this.canEditQuestionCode()) return;
    if (this.selectedQuestionId === question.questionCode) {
      this.selectedQuestionId = newCode;
    }
    question.questionCode = newCode;
  }

  onQuestionOrderChange(question: QuestionModel, newOrder: number): void {
    if (!this.canEdit()) return;
    question.questionOrder = newOrder;
    if (!Number.isFinite(newOrder)) return;

    // Stable sort, but let the edited question win ties so typing e.g. "4" inserts it at that
    // rank instead of just landing next to whichever question already held that value.
    const sorted = [...this.questions].sort((a, b) => {
      if (a.questionOrder !== b.questionOrder) return a.questionOrder - b.questionOrder;
      return a === question ? -1 : b === question ? 1 : 0;
    });

    // Push every question that no longer has room strictly after the one before it, cascading
    // forward only as far as needed — everyone else's existing gaps are left untouched.
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].questionOrder <= sorted[i - 1].questionOrder) {
        sorted[i] = { ...sorted[i], questionOrder: sorted[i - 1].questionOrder + 1 };
      }
    }

    this.questions = sorted;
  }

  addQuestion(questionType: QuestionType): void {
    if (!this.canEdit()) return;
    this.closeAddMenu();
    let question = this.getDefaultConfig(questionType);
    question.questionCode = this.generateQuestionId();
    question.title = 'Untitled question';
    question.questionType = questionType;
    question.mandatory = false;
    question.questionOrder = Math.max(0, ...this.questions.map(q => q.questionOrder)) + 1;

    this.questions.push(question);
    this.selectedQuestionId = question.questionCode;
    this.scrollToQuestion(question.questionCode);
  }

  changeQuestionType(question: QuestionModel, questionType: QuestionType): void {
    if (!this.canEdit()) return;
    question.questionType = Number(questionType);
    // "Not Applicable" only exists for some types — don't carry it over to the others.
    if (!this.supportsNa(question)) question.na = null;
    question = this.getDefaultConfig(questionType, question);
  }

  isBasicQuestionType(question: QuestionModel): boolean {
    return this.basicQuestionTypes.includes(question.questionType);
  }

  // ── Not Applicable (Numeric and basic types) ─────────────────────────────
  // na === null means disabled; any string (even '') means enabled.

  supportsNa(question: QuestionModel): boolean {
    return this.naQuestionTypes.includes(question.questionType);
  }

  isNaEnabled(question: QuestionModel): boolean {
    return question.na !== null && question.na !== undefined;
  }

  toggleNa(question: QuestionModel, enabled: boolean): void {
    if (!this.canEdit()) return;
    question.na = enabled ? '' : null;
  }

  // Only lowercase letters, digits and underscore are allowed. Input is normalised as it's typed
  // (uppercase lowered, anything else dropped) rather than rejected, and the field is written back
  // directly because ngModel won't re-render when the cleaned value equals the previous one.
  onNaInput(question: QuestionModel, input: HTMLInputElement): void {
    if (!this.canEdit()) return;
    const cleaned = input.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    input.value = cleaned;
    question.na = cleaned;
  }

  isNaMissing(question: QuestionModel): boolean {
    return this.supportsNa(question) && this.isNaEnabled(question) && !question.na;
  }

  get hasMissingNa(): boolean {
    return this.questions.some(q => this.isNaMissing(q));
  }

  requestDeleteQuestion(question: QuestionModel, event?: MouseEvent): void {
    if (!this.canEdit()) return;
    event?.stopPropagation();
    this.deleteError.set(null);
    this.questionPendingDelete.set(question);
  }

  confirmDelete(): void {
    if (!this.canEdit()) return;
    const question = this.questionPendingDelete();
    const organizationId = this.organizationStore.selectedOrganizationId();
    const surveyId = this.survey?.id;

    if (!question || !organizationId || !surveyId) return;

    this.deleting.set(true);
    this.surveyModelService.deleteSurveyModel(organizationId, surveyId, [question.questionCode]).subscribe({
      next: (response) => {
        this.deleting.set(false);
        if (!response.isFullSuccess) {
          this.deleteError.set(response.failed.map(f => f.reason).join(', ') || 'Failed to delete question.');
          return;
        }
        this.deleteQuestion(question);
        this.normalizeQuestionOrder();
        this.takeSnapshot();
        this.closeQuestionMenu();
        this.questionPendingDelete.set(null);
      },
      error: (err) => {
        this.deleting.set(false);
        this.deleteError.set('Failed to delete question. | '+ err);
      }
    });
  }

  cancelDelete(): void {
    if (this.deleting()) return;
    this.deleteError.set(null);
    this.questionPendingDelete.set(null);
  }

  // ── Change tracking ─────────────────────────────────────────────────────────

  private takeSnapshot(): void {
    this.originalQuestions.clear();
    for (const q of this.questions) {
      this.originalQuestions.set(q.questionCode, JSON.stringify(q));
    }
  }

  getQuestionChangeStatus(question: QuestionModel): 'new' | 'modified' | 'clean' {
    const original = this.originalQuestions.get(question.questionCode);
    if (!original) return 'new';
    return JSON.stringify(question) === original ? 'clean' : 'modified';
  }

  get pendingChangesCount(): number {
    return this.questions.filter(q => this.getQuestionChangeStatus(q) !== 'clean').length;
  }

  get newQuestionsCount(): number {
    return this.questions.filter(q => this.getQuestionChangeStatus(q) === 'new').length;
  }

  get modifiedQuestionsCount(): number {
    return this.questions.filter(q => this.getQuestionChangeStatus(q) === 'modified').length;
  }

  private get pendingChangedQuestions(): QuestionModel[] {
    return this.questions.filter(q => this.getQuestionChangeStatus(q) !== 'clean');
  }

  // ── Save ────────────────────────────────────────────────────────────────────

  requestSave(): void {
    if (!this.canEdit() || this.hasDuplicateChoiceValues || this.hasMissingNa) return;
    this.saveError.set(null);
    this.showSaveConfirm.set(true);
  }

  confirmSave(): void {
    if (!this.canEdit()) return;
    const organizationId = this.organizationStore.selectedOrganizationId();
    const surveyId = this.survey?.id;
    if (!organizationId || !surveyId) return;

    const record: Record<string, QuestionModel> = {};
    for (const q of this.pendingChangedQuestions) {
      record[q.questionCode] = q;
    }

    this.saving.set(true);
    this.surveyModelService.patchSurveyModel(organizationId, surveyId, record).subscribe({
      next: (response) => {
        this.saving.set(false);
        if (!response.isFullSuccess) {
          this.saveError.set(response.failed.map(f => f.reason).join(', ') || 'Some questions failed to save.');
          return;
        }
        this.showSaveConfirm.set(false);
        this.loadSurveyModel(organizationId, surveyId);
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set('Failed to save. Please try again. | '+ err);
      }
    });
  }

  cancelSave(): void {
    if (this.saving()) return;
    this.saveError.set(null);
    this.showSaveConfirm.set(false);
  }

  // ── Discard ─────────────────────────────────────────────────────────────────

  requestDiscard(): void {
    if (!this.canEdit()) return;
    this.showDiscardConfirm.set(true);
  }

  confirmDiscard(): void {
    if (!this.canEdit()) return;
    this.questions = Array.from(this.originalQuestions.entries())
      .map(([, json]) => JSON.parse(json) as QuestionModel)
      .sort((a, b) => a.questionOrder - b.questionOrder);
    this.selectedQuestionId = this.questions[0]?.questionCode ?? '';
    this.showDiscardConfirm.set(false);
  }

  cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  // ── Leave guard ─────────────────────────────────────────────────────────────

  setLeaveResolve(fn: (allow: boolean) => void): void {
    this.leaveResolve = fn;
  }

  confirmLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(true);
    this.leaveResolve = null;
  }

  cancelLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(false);
    this.leaveResolve = null;
  }

  deleteQuestion(question: QuestionModel): void {
    this.questions = this.questions.filter(q => q.questionCode !== question.questionCode);
    this.selectedQuestionId = this.questions[0]?.questionCode;
  }

  private getDefaultConfig(questionType: QuestionType, question: QuestionModel | null = null): QuestionModel {
    if(!question){
      question = {
        questionCode: 'new_Q_1',
        previousQuestionCode: '',
        groupId: null,
        skipCondition: null,
        displayCondition: null,
        questionType: QuestionType.Numeric,
        title: 'How satisfied are you?',
        description: null,
        placeHolderLabel: null,
        choices: [],
        multiFields: [],
        minValue: 1,
        maxValue: 10,
        scaleType: '',
        step: 1,
        na: null,
        questionOrder: 1,
        mandatory: true,
        answer: '',
        additionalValue: null,
        answerType: null,
        surveyPartialResponses: false,
        reccomandationScore: false,
        choicheDataSource: null,
        privacyFlag: false,
        forceChoiceOrder: false,
        imageMapUrl: null,
        dataSourceId: null,
      };
    }

    switch (questionType) {
      case QuestionType.Numeric:
        question.minValue = 1;
        question.maxValue = 10;
        question.step = 1;
        break;

      case QuestionType.MultipleChoice:
        question.choices = [
          {
            choiceText: 'Option 1',
            choiceValue: 'C1',
            choiceOrder: 1,
            choiceData: '',
            choiceType: 0
          },
          {
            choiceText: 'Option 2',
            choiceValue: 'C2',
            choiceOrder: 2,
            choiceData: '',
            choiceType: 0
          },
        ];
        break;

      case QuestionType.Verbatim:
        question.placeHolderLabel = 'Type your answer...';
        question.maxValue = 500;
        break;

      case QuestionType.Boolean:
        question.choices = [
          {
            choiceText: 'No',
            choiceValue: '0',
            choiceOrder: 1,
            choiceData: '',
            choiceType: 0
          },
          {
            choiceText: 'Yes',
            choiceValue: '1',
            choiceOrder: 2,
            choiceData: '',
            choiceType: 0
          },
        ];
        break;
    }

    return question;
  }

  generateQuestionId(): string {
    let counter = this.questions.length + 1;
    let id = `new_Q_${counter}`;

    while (this.questions.some(q => q.questionCode === id)) {
      counter++;
      id = `new_Q_${counter}`;
    }
    return id;
  }

  get orderedQuestions(): QuestionModel[] {
    return [...this.questions].sort(
      (a, b) => a.questionOrder - b.questionOrder
    );
  }

  toggleSurveyMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.closeQuestionMenu();
    this.closeAddMenu();
    this.openSurveyMenu = true;
  }

  closeSurveyMenu(): void {
    this.openSurveyMenu = false;
  }

  toggleAddMenu(event: MouseEvent): void {
    if (!this.canEdit()) return;
    event.stopPropagation();
    this.closeSurveyMenu();
    this.closeQuestionMenu();
    this.openAddMenu = !this.openAddMenu;
  }

  closeAddMenu(): void {
    this.openAddMenu = false;
  }

  toggleQuestionMenu(questionId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.closeSurveyMenu();
    this.closeAddMenu();
    this.openMenuQuestionId = this.openMenuQuestionId === questionId ? null : questionId;
  }

  closeQuestionMenu(): void {
    this.openMenuQuestionId = null;
  }

  moveQuestionUp(question: QuestionModel, event: MouseEvent): void {
    if (!this.canEdit()) return;
    event.stopPropagation();

    const ordered = this.orderedQuestions;
    const index = ordered.findIndex(q => q.questionCode === question.questionCode);

    if (index <= 0) {
      return;
    }

    const previous = ordered[index - 1];

    [question.questionOrder, previous.questionOrder] = [
      previous.questionOrder,
      question.questionOrder,
    ];

    this.normalizeQuestionOrder();
    this.closeQuestionMenu();
  }

  moveQuestionDown(question: QuestionModel, event: MouseEvent): void {
    if (!this.canEdit()) return;
    event.stopPropagation();

    const ordered = this.orderedQuestions;
    const index = ordered.findIndex(q => q.questionCode === question.questionCode);

    if (index === -1 || index >= ordered.length - 1) {
      return;
    }

    const next = ordered[index + 1];

    [question.questionOrder, next.questionOrder] = [next.questionOrder, question.questionOrder];

    this.normalizeQuestionOrder();
    this.closeQuestionMenu();
  }

  onQuestionDrop(event: CdkDragDrop<QuestionModel[]>): void {
    if (!this.canEdit() || event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
    this.questions = this.questions.map((q, i) => ({ ...q, questionOrder: i + 1 }));
    this.closeQuestionMenu();
  }

  duplicateQuestion(question: QuestionModel, event: MouseEvent): void {
    if (!this.canEdit()) return;
    event.stopPropagation();

    const ordered = this.orderedQuestions;
    const index = ordered.findIndex(q => q.questionCode === question.questionCode);

    const duplicated: QuestionModel = {
      ...structuredClone(question),
      questionCode: this.generateQuestionId(),
      title: `${question.title} Copy`,
      questionOrder: question.questionOrder + 1,
    };

    ordered.splice(index + 1, 0, duplicated);

    // Leave everything up to and including the duplicate untouched. Only push forward the questions after it.
    for (let i = index + 2; i < ordered.length && ordered[i].questionOrder <= ordered[i - 1].questionOrder; i++) {
      ordered[i] = { ...ordered[i], questionOrder: ordered[i - 1].questionOrder + 1 };
    }

    this.questions = ordered;
    this.selectedQuestionId = duplicated.questionCode;
    this.scrollToQuestion(duplicated.questionCode);
    this.closeQuestionMenu();
  }

  private scrollToQuestion(questionCode: string): void {
    setTimeout(() => {
      document.getElementById('question-' + questionCode)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  deleteQuestionFromMenu(question: QuestionModel, event: MouseEvent): void {
    this.requestDeleteQuestion(question, event);
  }

  private normalizeQuestionOrder(): void {
    this.questions = this.orderedQuestions.map((q, i) => ({
      ...q,
      questionOrder: i + 1,
    }));
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.pendingChangesCount > 0) {
      event.preventDefault();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('[data-survey-menu]')) {
      this.closeSurveyMenu();
    }
    if (!target.closest('[data-add-menu]')) {
      this.closeAddMenu();
    }
    if (!target.closest('[data-question-menu]')) {
      this.closeQuestionMenu();
    }
  }

  getOrderedChoices(question: QuestionModel): ChoiceModel[] {
    return [...(question.choices ?? [])].sort(
      (a, b) => a.choiceOrder - b.choiceOrder
    );
  }

  getBooleanChoiceText(question: QuestionModel, choiceValue: '0' | '1'): string {
    return question.choices.find(c => c.choiceValue === choiceValue)?.choiceText ?? '';
  }

  setBooleanChoiceText(question: QuestionModel, choiceValue: '0' | '1', text: string): void {
    if (!this.canEdit()) return;
    const choice = question.choices.find(c => c.choiceValue === choiceValue);
    if (choice) choice.choiceText = text;
  }

  addChoice(question: QuestionModel): void {
    if (!this.canEdit()) return;
    question.choices ??= [];

    question.choices.push({
      choiceText: `Option ${question.choices.length + 1}`,
      choiceValue: this.generateChoiceId(question),
      choiceOrder: question.choices.length + 1,
      choiceData: '',
      choiceType: 0
    });
  }

  removeChoice(question: QuestionModel, choice: ChoiceModel): void {
    if (!this.canEdit()) return;
    question.choices = question.choices.filter(c => c !== choice);

    this.normalizeChoiceOrder(question);
  }

  moveChoiceUp(question: QuestionModel, choice: ChoiceModel): void {
    if (!this.canEdit()) return;
    const ordered = this.getOrderedChoices(question);
    const index = ordered.findIndex(c => c === choice);

    if (index <= 0) {
      return;
    }

    const previous = ordered[index - 1];

    [choice.choiceOrder, previous.choiceOrder] = [
      previous.choiceOrder,
      choice.choiceOrder,
    ];

    this.normalizeChoiceOrder(question);
  }

  moveChoiceDown(question: QuestionModel, choice: ChoiceModel): void {
    if (!this.canEdit()) return;
    const ordered = this.getOrderedChoices(question);
    const index = ordered.findIndex(c => c === choice);

    if (index === -1 || index >= ordered.length - 1) {
      return;
    }

    const next = ordered[index + 1];

    [choice.choiceOrder, next.choiceOrder] = [
      next.choiceOrder,
      choice.choiceOrder,
    ];

    this.normalizeChoiceOrder(question);
  }

  private normalizeChoiceOrder(question: QuestionModel): void {
    question.choices = this.getOrderedChoices(question).map((choice, index) => ({
      ...choice,
      choiceOrder: index + 1,
    }));
  }

  isDuplicateChoiceValue(question: QuestionModel, choice: ChoiceModel): boolean {
    const value = choice.choiceValue?.trim();
    if (!value) return false;
    return (question.choices ?? []).some(c => c !== choice && c.choiceValue?.trim() === value);
  }

  hasDuplicateChoiceValuesIn(question: QuestionModel): boolean {
    return (question.choices ?? []).some(c => this.isDuplicateChoiceValue(question, c));
  }

  get hasDuplicateChoiceValues(): boolean {
    return this.questions.some(q => this.hasDuplicateChoiceValuesIn(q));
  }

  generateChoiceId(question: QuestionModel): string {
    const choices = question.choices ?? [];
    let counter = choices.length + 1;
    let id = `C${counter}`;

    while (choices.some(choice => choice.choiceValue === id)) {
      counter++;
      id = `C${counter}`;
    }
    return id;
  }


  changeTranslations(){
    for(let q of this.questions){
      q.title = this.translations?.translations.find(t => t.questionCode == q.questionCode && t.choiceValue == null && t.culture == this.defaultLanguage)?.localizedText ?? q.title ?? '';
      
      if(q.choices.length){
        for(let c of q.choices){
          c.choiceText = this.translations?.translations.find(t => t.questionCode == q.questionCode && t.choiceValue == c.choiceValue && t.culture == this.defaultLanguage)?.localizedText ?? c.choiceText ?? '';
        }
      }
    }
  }
}